import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) {
      setPlatform('ios');
    } else if (/android/i.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    if (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) {
      setInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setDeferredPrompt(null);
  };

  if (installed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-md">
          <img src="/assets/img/logo.png" alt="SkillsNetPro" className="mx-auto mb-6 h-12" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h1>
          <p className="text-gray-600">SkillsNetPro is installed. Open it from your home screen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md">
        <img src="/assets/img/logo.png" alt="SkillsNetPro" className="mx-auto mb-6 h-12" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Get the SkillsNetPro App</h1>
        <p className="text-gray-600 mb-8">Access your network right from your home screen.</p>

        {platform === 'android' && deferredPrompt && (
          <button
            onClick={handleInstall}
            className="w-full py-3 px-6 rounded-lg text-white font-semibold text-lg"
            style={{ backgroundColor: '#EF7A2B' }}
          >
            Install App
          </button>
        )}

        {platform === 'android' && !deferredPrompt && (
          <div className="text-left bg-white rounded-lg p-6 shadow-sm">
            <p className="font-semibold text-gray-900 mb-3">To install:</p>
            <ol className="space-y-2 text-gray-700">
              <li>1. Tap the <strong>⋮</strong> menu in Chrome</li>
              <li>2. Tap <strong>"Add to Home screen"</strong></li>
              <li>3. Tap <strong>"Add"</strong></li>
            </ol>
          </div>
        )}

        {platform === 'ios' && (
          <div className="text-left bg-white rounded-lg p-6 shadow-sm">
            <p className="font-semibold text-gray-900 mb-3">To install on iPhone:</p>
            <ol className="space-y-2 text-gray-700">
              <li>1. Tap the <strong>Share</strong> button <span className="text-xl">&#x2191;</span> at the bottom of Safari</li>
              <li>2. Scroll down and tap <strong>"Add to Home Screen"</strong></li>
              <li>3. Tap <strong>"Add"</strong> in the top right</li>
            </ol>
            <p className="mt-4 text-sm text-gray-500">Note: This must be done in Safari, not Chrome or other browsers.</p>
          </div>
        )}

        {platform === 'desktop' && (
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <p className="text-gray-700">Open this page on your phone to install the app.</p>
            <p className="mt-3 text-sm text-gray-500">Visit this URL on your mobile device's browser.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Install;
